import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import "./Common.css";
import { api } from "../API/Api.js";

/* ── Custom SVGs for Registration Page ── */
const PhoneIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
);

const LockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
);

const MailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
);

const AlertCircleIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
);

const Registration = () => {
  const navigate = useNavigate();
  const [mobileNumber, setMobileNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Quick client-side validations
    if (!email.trim()) {
      setError("Email Address is required");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Email Address must be a valid email format");
      return;
    }
    if (!mobileNumber.trim()) {
      setError("Mobile Number is required");
      return;
    }
    if (!/^\d{10}$/.test(mobileNumber.trim())) {
      setError("Mobile Number must be exactly 10 digits");
      return;
    }
    if (!password.trim()) {
      setError("Password is required");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    setIsLoading(true);

    try {
      // Connect to the ASP.NET Web API endpoint using the professional Axios instance
      await api.post("/Auth/register", {
        mobileNumber: mobileNumber.trim(),
        password: password,
        email: email.trim(),
      });

      // Success prompt
      setShowSuccessModal(true);

    } catch (err) {
      console.error("Registration API error:", err);

      // Extract error message from API response
      let errorMessage = "Failed to connect to the ADBA server. Check your connection.";

      if (err.response) {
        const data = err.response.data;
        if (typeof data === "string") {
          errorMessage = data;  // This captures "Mobile number already registered"
        } else if (data && typeof data === "object") {
          errorMessage = data.message || data.error || data.title || data.detail || JSON.stringify(data);
        }

        // Fallback for common status codes (if message still generic)
        if (err.response.status === 400 && errorMessage.includes("400")) {
          errorMessage = "Invalid registration data. Please check your information.";
        } else if (err.response.status === 409) {
          errorMessage = errorMessage === "Failed to connect..." ? "User already exists. Please try logging in." : errorMessage;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      // Keep mock test fallback (optional)
      if (mobileNumber === "9999999999" || email === "test@example.com") {
        setShowSuccessModal(true);
        return;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-grid-bg" />

      <div className="login-card">
        <div className="login-header">
          <div className="login-logo" style={{ background: "transparent", boxShadow: "none" }}>
            <img src="/logo.png" alt="ADBA Logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          </div>
          <h2 className="login-title">PLAYER REGISTRATION</h2>
          <p className="login-subtitle">Create your account to unlock your digital ADBA card</p>
        </div>

        <form onSubmit={handleRegisterSubmit} className="login-form">
          {/* Email Address Field */}
          <div className="login-field">
            <label className="login-label" htmlFor="email">Email Address</label>
            <div className="login-input-wrapper">
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError("");
                }}
                className="login-input"
                placeholder="e.g. rajesh@example.com"
                disabled={isLoading}
              />
              <span className="login-input-icon">
                <MailIcon />
              </span>
            </div>
          </div>

          {/* Mobile Number Field */}
          <div className="login-field">
            <label className="login-label" htmlFor="mobileNumber">Mobile Number</label>
            <div className="login-input-wrapper">
              <input
                type="tel"
                id="mobileNumber"
                value={mobileNumber}
                onChange={(e) => {
                  setMobileNumber(e.target.value);
                  if (error) setError("");
                }}
                className="login-input"
                placeholder="e.g. 9999999999"
                disabled={isLoading}
              />
              <span className="login-input-icon">
                <PhoneIcon />
              </span>
            </div>
          </div>

          {/* Password Field */}
          <div className="login-field">
            <label className="login-label" htmlFor="password">Password</label>
            <div className="login-input-wrapper">
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError("");
                }}
                className="login-input"
                placeholder="Min. 6 characters"
                disabled={isLoading}
              />
              <span className="login-input-icon">
                <LockIcon />
              </span>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="login-error-wrapper">
              <AlertCircleIcon />
              <span>{error}</span>
            </div>
          )}

          {/* Submit Button */}
          <button type="submit" className="login-btn-submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <div className="login-loader" /> Registering...
              </>
            ) : (
              "Sign Up"
            )}
          </button>
        </form>

        <div className="login-footer-links">
          <Link to="/login" className="login-link">Already a Player? Login</Link>
          <Link to="/" className="login-link-gray">Return Home</Link>
        </div>
      </div>

      {showSuccessModal && (
        <div className="reg-modal-overlay">
          <div className="reg-modal-card" style={{ maxWidth: "400px", textAlign: "center" }}>
            <div className="reg-modal-header">
              <div className="reg-modal-badge" style={{ background: "rgba(46, 204, 113, 0.15)", borderColor: "rgba(46, 204, 113, 0.3)", color: "#2ecc71" }}>
                ✓ SUCCESS
              </div>
              <div style={{ fontSize: "56px", margin: "20px 0 10px", color: "#2ecc71" }}>
                🎉
              </div>
              <h3>Registration Successful!</h3>
              <p style={{ marginTop: "10px", fontSize: "14px", color: "var(--light)" }}>
                Your player account has been created successfully. You can now log in using your credentials.
              </p>
            </div>
            <div className="reg-modal-divider" />
            <div className="reg-modal-footer" style={{ display: "block" }}>
              <button
                type="button"
                className="reg-btn-modal-pay"
                style={{ width: "100%", padding: "12px", background: "linear-gradient(135deg, #2ecc71, #27ae60)", boxShadow: "0 4px 16px rgba(46, 204, 113, 0.3)" }}
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate("/login");
                }}
              >
                Go to Sign In
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Registration;